<script lang="ts" setup>
// ================================
// IMPORTS
// ================================

// Vue
import { computed, ref, inject } from "vue"
import { useI18n } from "vue-i18n"

// Composables
import { useRedirect } from "@core/composables"
import { useEventBus, useClipboard } from "@vueuse/core"

// Stores
import { useAuthStore } from "@core/stores/auth.store"

// Constants
import {
    CONSENT_STATUS,
    PCE_DDA_STATUS,
    SOURCES_TYPES,
    SOURCE_TYPE_CATEGORIES,
} from "@core/constants"

// Helpers
import {
    formatIdSource,
    downloadPDF,
    isCurrentUserHolderForExtend,
    plainAddressForExtend,
    signataireToExtendParty,
} from "@core/helpers"
import moment from "moment"

// PrimeVue
import { ToastMessageOptions, useToast } from "primevue"

// Types
import { ConsentementLegacy, ColumnConfig, ConsentementSource } from "@types"
import type { Services } from "@services"

// Components
import type {
    SourceExtendDialogDonePayload,
    SourceExtendDialogMeter,
} from "@core/components/source/meter/form/extend/SourceExtendDialog.vue"
import {
    CONSENT_REQUESTS_RELOAD_BUS_KEY,
    type ConsentExtendListPatchFields,
    type ConsentListPatch,
} from "./consent.events"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            compteur: "Compteur",
            type: "Type",
            titulaire: "Titulaire",
            name: "Nom",
            state: "État",
            grdf_access: "Accès GRDF",
            copy_number: "Copier le numéro",
            tooltip: {
                copy: {
                    notification: "Numéro copié dans le presse-papier",
                },
            },
            download: "Télécharger",
            revoke: "Révoquer",
            change_owner: "Changer de titulaire",
            extend: ({ named }: { named: (key: string) => string }) =>
                named("isOwner") ? "Prolonger" : "Demander le prolongement",
            status: ({ named }: { named: (key: string) => string }) => {
                const statusMap: Record<
                    "active" | "expired" | "revoked" | "no-sign",
                    string
                > = {
                    active: "Actif",
                    expired: "Expiré",
                    revoked: "Révoqué",
                    "no-sign": "Non signé",
                }

                const key = named("status") as keyof typeof statusMap

                return statusMap[key] ?? "Non signé"
            },
            statusDDA: ({ named }: { named: (key: string) => string }) => {
                const status = {
                    [PCE_DDA_STATUS.ACTIVE]: "Actif",
                    [PCE_DDA_STATUS.OBSOLETE]: "Obsolète",
                    [PCE_DDA_STATUS.A_VALIDER]: "À valider",
                    [PCE_DDA_STATUS.REFUSED]: "Refusée",
                    [PCE_DDA_STATUS.ERROR]: "Erreur",
                    [PCE_DDA_STATUS.REVOKED]: "Révoquée",
                    [PCE_DDA_STATUS.RECHECK]: "Vérification GRDF",
                    [PCE_DDA_STATUS.EMPTY]: "Non déclaré",
                }
                return status[named("status")] ?? "Inconnu"
            },
            empty_list: "Aucun consentement trouvé",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    consents: ConsentementLegacy[]
    rowsPerPage: number
}>()

/**
 * Mise à jour locale de la liste des consentements (révocation, prolongement signé) sans refetch.
 */
const emit = defineEmits<{
    (e: "patch-consent-list", patch: ConsentListPatch): void
}>()

/** Payload `update:consent` du dialog prolongement (signature directe uniquement). */
type SourceExtendConsentUpdatePayload = {
    consentId?: string
    sourceId?: string
    token?: string
    consentementFields?: ConsentExtendListPatchFields
}

// ================================
// SERVICES & STORES
// ================================

/**
 * Store d'authentification pour vérifier les permissions utilisateur
 */
const authStore = useAuthStore()

/**
 * Service de récupération des consentements
 */
const { $getPreviewConsent } = inject<Services>("services")!

/**
 * Bus d'événements pour les notifications de tâches
 */
const busTask = useEventBus("task")

/**
 * Bus d'événements pour les notifications
 */
const bus = useEventBus("notification")

/**
 * Bus pour recharger les demandes de consentement (ConsentRequestCard) après prolongement / changement titulaire.
 */
const busConsentRequestsReload = useEventBus<void>(
    CONSENT_REQUESTS_RELOAD_BUS_KEY
)

/**
 * Composable pour copier dans le presse-papier
 */
const { copy } = useClipboard()

const { redirectWrapper } = useRedirect()

/**
 * Service de notifications toast
 */
const toast = useToast()

// ================================
// ÉTAT DU MENU CONTEXTUEL
// ================================

/**
 * Référence au composant menu contextuel
 */
const menu = ref()

/**
 * Consentement sélectionné pour le menu contextuel
 */
const selectedConsent = ref<ConsentementLegacy | null>(null)

/**
 * État d'affichage de la modale de révocation de consentement
 */
const modalMeterRevokeConsentToggle = ref<boolean>(false)

/** Dialog prolongement (remplace postMessage vers iframe). */
const extendDialogVisible = ref(false)
const extendDialogMeter = ref<SourceExtendDialogMeter | null>(null)
const extendDialogBuildingId = ref("")
/** `_id` consentement au moment d’ouvrir le prolongement (fusion liste après signature directe). */
const extendDialogConsentIdAtOpen = ref<string | null>(null)
/** true = parcours titulaire avec signature dans le dialog (pas invitation). */
const extendDialogIsDirectSignPath = ref(false)
/** Dernière réponse API reçue via `update:consent` sur signature directe. */
const extendSignedServicePayload = ref<SourceExtendConsentUpdatePayload | null>(
    null
)

/** Dialog changement de titulaire (remplace postMessage vers iframe). */
const changeOwnerDialogVisible = ref(false)
const changeOwnerDialogMeter = ref<SourceExtendDialogMeter | null>(null)
const changeOwnerDialogBuildingId = ref("")

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Vérifie si l'utilisateur connecté est le propriétaire du consentement
 * @returns True si l'utilisateur est propriétaire, false sinon
 */
const getIsOwner = computed((): boolean => {
    if (!selectedConsent.value) return false

    // si le signataire est une personne physique alors on vérifie que c'est bien la même personne qui a signé le consentement
    if (selectedConsent.value?.signataire?.personne === "Physique") {
        return (
            authStore.userId ===
            selectedConsent.value?.signataire?.user?._id_user
        )
    }
    // si le signataire est une personne morale alors on a deux possibilités
    // 1 - l'utilisateur connecté est admin et appartient à la même entreprise qui a signé le consentement
    // 2 - l'utilisateur connecté est le signataire du consentement
    return (
        (authStore.companyId ===
            selectedConsent.value?.signataire?.company?._id_company &&
            authStore.isCompanyAdmin) ||
        authStore.userId === selectedConsent.value?.signataire?.user?._id_user
    )
})

/**
 * Titulaire / signataire pour le prolongement : même `_id_company` (morale) ou
 * même utilisateur / e-mail (physique). Ne dépend pas du flag admin entreprise.
 */
const getIsExtendConsentHolder = computed((): boolean => {
    if (!selectedConsent.value?.signataire) return false
    return isCurrentUserHolderForExtend(
        signataireToExtendParty(selectedConsent.value.signataire),
        {
            userId: authStore.userId,
            username: authStore.username,
            companyId: authStore.companyId,
        }
    )
})

/**
 * Ouvre {@link SourceExtendDialog} pour la ligne sélectionnée du menu.
 */
function onOpenExtendConsentDialog(): void {
    const c = selectedConsent.value
    if (!c?.source || !c.signataire?.user) return
    const src = c.source
    const sign = c.signataire
    extendDialogMeter.value = {
        _id: src._id_source,
        idtype: src.idtype,
        idsource: src.idsource,
        address: plainAddressForExtend(src.address),
        owner: {
            _id: sign.user._id_user ?? undefined,
            firstname: sign.user.firstname ?? "",
            lastname: sign.user.lastname ?? "",
            username: sign.user.username ?? "",
            company: sign.company?.name ?? null,
            companyType: sign.company?.type ?? null,
        },
    }
    const srcWithBuilding = src as ConsentementSource & {
        _id_building?: string
    }
    extendDialogBuildingId.value = srcWithBuilding._id_building ?? ""
    extendDialogConsentIdAtOpen.value = c._id
    extendDialogIsDirectSignPath.value = getIsExtendConsentHolder.value
    extendSignedServicePayload.value = null
    extendDialogVisible.value = true
}

/**
 * Réception des données API après signature directe (avant `done`).
 */
function onSourceExtendUpdateConsent(
    payload: SourceExtendConsentUpdatePayload
): void {
    if (extendDialogIsDirectSignPath.value) {
        extendSignedServicePayload.value = payload
    }
}

/**
 * Après prolongement : demandes si invitation ; patch liste si signature directe ; rien si abandon.
 */
function onExtendDialogDone(payload: SourceExtendDialogDonePayload): void {
    extendDialogVisible.value = false
    extendDialogMeter.value = null
    if (payload.kind === "invitation_completed") {
        extendSignedServicePayload.value = null
        extendDialogConsentIdAtOpen.value = null
        extendDialogIsDirectSignPath.value = false
        busConsentRequestsReload.emit()
    } else if (payload.kind === "signed_direct") {
        const prevId = extendDialogConsentIdAtOpen.value
        const svc = extendSignedServicePayload.value
        extendSignedServicePayload.value = null
        extendDialogConsentIdAtOpen.value = null
        extendDialogIsDirectSignPath.value = false
        if (prevId) {
            emit("patch-consent-list", {
                kind: "extend_signed",
                previousConsentId: prevId,
                newConsentId: svc?.consentId,
                fields: svc?.consentementFields,
            })
        }
    } else {
        extendSignedServicePayload.value = null
        extendDialogConsentIdAtOpen.value = null
        extendDialogIsDirectSignPath.value = false
    }
}

function onExtendDialogCancel(): void {
    extendDialogMeter.value = null
    extendSignedServicePayload.value = null
    extendDialogConsentIdAtOpen.value = null
    extendDialogIsDirectSignPath.value = false
}

/**
 * Après révocation confirmée : marque le consentement comme révoqué dans la liste locale.
 */
function onRevokeConfirmed(): void {
    const id = selectedConsent.value?._id
    if (id) {
        emit("patch-consent-list", { kind: "revoked", consentId: id })
    }
}

function onOpenChangeOwnerDialog(): void {
    const c = selectedConsent.value
    if (!c?.source || !c.signataire?.user) return
    const src = c.source
    const sign = c.signataire
    changeOwnerDialogMeter.value = {
        _id: src._id_source,
        idtype: src.idtype,
        idsource: src.idsource,
        address: src.address ?? null,
        owner: {
            _id: sign.user._id_user ?? undefined,
            firstname: sign.user.firstname ?? "",
            lastname: sign.user.lastname ?? "",
            username: sign.user.username ?? "",
            company: sign.company?.name ?? null,
            companyType: sign.company?.type ?? null,
        },
    }
    const srcWithBuilding = src as ConsentementSource & {
        _id_building?: string
    }
    changeOwnerDialogBuildingId.value = srcWithBuilding._id_building ?? ""
    changeOwnerDialogVisible.value = true
}

/**
 * Après changement de titulaire : met à jour uniquement les demandes de consentement (bus), sans `update:consent` sur le parent.
 */
function onChangeOwnerDialogDone(): void {
    changeOwnerDialogVisible.value = false
    changeOwnerDialogMeter.value = null
    busConsentRequestsReload.emit()
}

function onChangeOwnerDialogCancel(): void {
    changeOwnerDialogMeter.value = null
}

/**
 * Éléments du menu contextuel selon les permissions et le type de consentement
 * @returns Liste des actions disponibles dans le menu contextuel
 */
const getMenuItems = computed(() => {
    if (!selectedConsent.value) return []
    const tabs = [
        {
            label: t("copy_number"),
            command: onClickCopyIdSource,
        },
        {
            label: t("download"),
            command: async () => {
                let notification: ToastMessageOptions | null = null
                busTask.emit({
                    severity: "info",
                    title: "Téléchargement en cours",
                    message:
                        "Le consentement est en cours de génération, veuillez patienter...",
                    callback,
                })
                const data = await $getPreviewConsent(
                    selectedConsent.value?._id || ""
                )

                const promise = $getPreviewConsent(
                    selectedConsent.value?._id || ""
                )

                function callback(_notification: ToastMessageOptions) {
                    notification = _notification
                }

                if (promise) {
                    await promise
                    downloadPDF(data.base64Data, "consentement")
                    if (notification) toast.remove(notification)
                }
            },
        },
    ]

    const isConsentWithUserExisted =
        !!selectedConsent.value?.signataire?.user?._id_user

    // Si c'est un PDL ou PCE alors on ajoute l'option d'extension
    if (
        [SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            selectedConsent.value.source?.idtype
        ) &&
        isConsentWithUserExisted
    ) {
        tabs.splice(1, 0, {
            label: t("extend", { isOwner: getIsExtendConsentHolder.value }),
            command: onOpenExtendConsentDialog,
        })
    }

    // il peut révoqué le consentement si pas déjà révoqué + si l'utilisateur est
    // 1 - propriétaire de la source + client homeys avec un droit > employé
    // 2 - propriétaie de la source + particulier UC2 ou pro UC2
    const isClientHomeys =
        authStore.isClientHomeys && authStore.isEmployeWriteOrHigher
    const isUC2 = authStore.isUc2Particulier || authStore.isUc2Pro

    const canRevoke =
        !selectedConsent.value.dismissed &&
        getIsOwner.value &&
        (isClientHomeys || isUC2)

    if (canRevoke) {
        tabs.push({
            label: t("revoke"),
            command: () => {
                modalMeterRevokeConsentToggle.value = true
            },
        })
    }

    // si l'utilisateur est un UC1 alors il peut
    // changer le titulaire de la source par demande.
    // On vérifie aussi si le droit d'accès de la source est actif,
    // car seulement les DDA inactive sont autorisés à changer de titulaire
    const isDDAActive =
        selectedConsent.value.source?.idtype === SOURCES_TYPES.PCE &&
        [
            PCE_DDA_STATUS.ACTIVE,
            PCE_DDA_STATUS.A_VALIDER,
            PCE_DDA_STATUS.RECHECK,
        ].includes(selectedConsent.value.source?.etat_droit_acces || "")

    const canChangeOwner =
        authStore.isClientHomeys &&
        !isDDAActive &&
        authStore.isEmployeWriteOrHigher
    if (
        canChangeOwner &&
        [SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            selectedConsent?.value?.source?.idtype
        ) &&
        isConsentWithUserExisted
    ) {
        tabs.push({
            label: t("change_owner"),
            command: onOpenChangeOwnerDialog,
        })
    }
    return tabs
})

/**
 * Prépare les données pour le tableau en aplatissant les champs imbriqués
 * @returns Données formatées pour le tableau
 */
const getTableData = computed(() => {
    return props.consents.map((consent: ConsentementLegacy) => {
        const sourceIdsource = formatIdSource(
            consent?.source?.idsource,
            consent?.source?.idtype
        )
        const sourceIdtype = consent?.source?.idtype?.toUpperCase() ?? ""
        const sourceName = consent?.source?.name ?? ""

        const userData = consent.signataire
        const titulaire =
            userData?.company?.name ??
            (`${userData?.user?.firstname || ""} ${userData?.user?.lastname || ""}`.trim() ||
                "-")

        const status = mapStatusFromConsent(consent)
        const state = t("status", { status })

        const grdfAccess = consent?.source.etat_droit_acces
            ? t("statusDDA", {
                  status: consent?.source.etat_droit_acces,
              })
            : ""

        // Détermine le type de compteur pour le filtre (catégorie)
        const sourceTypeForFilter = getSourceTypeCategory(
            consent?.source?.idtype
        )

        // Détermine le titulaire pour le filtre
        const titulaireForFilter = getTitulaireCategory(consent)

        // Champs de recherche (idsource sans espaces pour la recherche)
        const searchIdsource = (consent?.source?.idsource || "").replace(
            /\s+/g,
            ""
        )

        return {
            ...consent,
            sourceIdsource,
            sourceIdtype,
            sourceName,
            titulaire,
            titulaireForFilter, // Pour le filtre
            state,
            stateForFilter: status, // Pour le filtre (utilise le code, pas le label)
            grdfAccess,
            grdfAccessForFilter: consent?.source?.etat_droit_acces || "", // Pour le filtre
            sourceTypeForFilter, // Pour le filtre
            _id_source: consent?.source?._id_source,
            // Champs de recherche pour la recherche globale
            searchIdsource, // idsource sans espaces
            searchType: sourceIdtype, // Type de source
            searchSourceName: sourceName, // Nom de la source
            searchTitulaire: titulaire, // Nom du titulaire
        }
    })
})

/**
 * Options de filtre pour le type de compteur (catégories)
 * @returns Liste des options de filtre pour le type
 */
const getTypeFilterOptions = computed(() => {
    return Object.values(SOURCE_TYPE_CATEGORIES).map((category) => ({
        label: category.label,
        value: category.label,
    }))
})

/**
 * Options de filtre pour l'état du consentement
 * @returns Liste des options de filtre pour l'état
 */
const getStateFilterOptions = computed(() => {
    return [
        {
            label: t("status", { status: CONSENT_STATUS.ACTIVE }),
            value: CONSENT_STATUS.ACTIVE,
        },
        {
            label: t("status", { status: CONSENT_STATUS.EXPIRED }),
            value: CONSENT_STATUS.EXPIRED,
        },
        {
            label: t("status", { status: CONSENT_STATUS.REVOKED }),
            value: CONSENT_STATUS.REVOKED,
        },
        {
            label: t("status", { status: CONSENT_STATUS.NO_SIGN }),
            value: CONSENT_STATUS.NO_SIGN,
        },
    ]
})

/**
 * Options de filtre pour l'accès GRDF
 * @returns Liste des options de filtre pour l'accès GRDF
 */
const getGrdfAccessFilterOptions = computed(() => {
    return [
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.ACTIVE }),
            value: PCE_DDA_STATUS.ACTIVE,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.OBSOLETE }),
            value: PCE_DDA_STATUS.OBSOLETE,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.A_VALIDER }),
            value: PCE_DDA_STATUS.A_VALIDER,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.REFUSED }),
            value: PCE_DDA_STATUS.REFUSED,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.ERROR }),
            value: PCE_DDA_STATUS.ERROR,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.REVOKED }),
            value: PCE_DDA_STATUS.REVOKED,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.RECHECK }),
            value: PCE_DDA_STATUS.RECHECK,
        },
        {
            label: t("statusDDA", { status: PCE_DDA_STATUS.EMPTY }),
            value: PCE_DDA_STATUS.EMPTY,
        },
    ]
})

/**
 * Options de filtre pour le titulaire
 * @returns Liste des options de filtre pour le titulaire
 */
const getTitulaireFilterOptions = computed(() => {
    return [
        { label: "Vous", value: "vous" },
        { label: "Personne physique", value: "personne_physique" },
        { label: "Personne morale", value: "personne_morale" },
    ]
})

/**
 * Configuration des colonnes du tableau de consentements
 * @returns Configuration des colonnes avec leurs transformations et actions
 */
const getColumns = computed<ColumnConfig[]>(() => {
    return [
        {
            field: "sourceIdsource",
            header: `${t("compteur")} (${props.consents?.length})`,
            link: true,
            style: { width: "13rem" },
        },
        {
            field: "sourceTypeForFilter",
            header: t("type"),
            style: { width: "5rem" },
            filterable: true,
            filterOptions: getTypeFilterOptions.value,
        },
        {
            field: "sourceName",
            header: t("name"),
            style: { width: "8rem" },
        },
        {
            field: "titulaireForFilter",
            header: t("titulaire"),
            style: { width: "10rem" },
            filterable: true,
            filterOptions: getTitulaireFilterOptions.value,
        },
        {
            field: "stateForFilter",
            header: t("state"),
            style: { width: "8rem" },
            filterable: true,
            filterOptions: getStateFilterOptions.value,
        },
        {
            field: "grdfAccessForFilter",
            header: t("grdf_access"),
            style: { width: "10rem" },
            filterable: true,
            filterOptions: getGrdfAccessFilterOptions.value,
        },
        {
            field: "actions",
            header: "",
        },
        // Colonnes cachées pour la recherche globale
        {
            field: "searchIdsource",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchType",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchSourceName",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
        {
            field: "searchTitulaire",
            header: "",
            style: {
                width: "0",
                padding: "0",
                visibility: "hidden",
            } as Record<string, string>,
        },
    ]
})

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Mappe le statut d'un consentement selon ses propriétés
 * @param consent - Consentement à analyser
 * @returns Le statut du consentement (REVOKED, NO_SIGN, EXPIRED, ACTIVE)
 */
function mapStatusFromConsent(consent: ConsentementLegacy) {
    if (consent.dismissed) return CONSENT_STATUS.REVOKED
    else if (!consent.signed) return CONSENT_STATUS.NO_SIGN
    else if (moment(consent.date_to).isBefore(moment()))
        return CONSENT_STATUS.EXPIRED
    else return CONSENT_STATUS.ACTIVE
}

/**
 * Détermine la catégorie du type de source pour le filtre
 * @param idtype - Type de source (pdl, pce, etc.)
 * @returns Label de la catégorie (Électricité, Gaz, RCU)
 */
function getSourceTypeCategory(idtype: string): string {
    if (!idtype) return ""
    const category = Object.values(SOURCE_TYPE_CATEGORIES).find((cat) =>
        cat.types.includes(idtype)
    )
    return category?.label || ""
}

/**
 * Détermine la catégorie du titulaire pour le filtre
 * @param consent - Consentement à analyser
 * @returns Catégorie du titulaire (vous, personne_physique, personne_morale)
 */
function getTitulaireCategory(consent: ConsentementLegacy): string {
    const isCurrentUser =
        consent.signataire?.user?._id_user === authStore.userId
    const isCurrentCompany =
        consent.signataire?.company?.name &&
        consent.signataire?.company?._id_company === authStore.companyId

    if (isCurrentUser || isCurrentCompany) {
        return "vous"
    }

    if (consent.signataire?.personne === "Physique") {
        return "personne_physique"
    }

    if (consent.signataire?.personne === "Morale") {
        return "personne_morale"
    }

    return ""
}

/**
 * Calcule la classe CSS pour le statut d'un consentement
 * @param data - Données du consentement
 * @returns Classe CSS correspondant au statut
 */
function getStatusClassConsent(data: ConsentementLegacy): string {
    const status = mapStatusFromConsent(data)

    if (
        status === CONSENT_STATUS.REVOKED ||
        status === CONSENT_STATUS.EXPIRED
    ) {
        return "text-red-600"
    } else if (status === CONSENT_STATUS.ACTIVE) {
        return "text-green-600"
    }
    return ""
}

/**
 * Calcule la classe CSS pour le statut d'accès GRDF
 * @param data - Données du consentement
 * @returns Classe CSS correspondant au statut DDA
 */
function getStatusClassDDA(data: ConsentementLegacy): string {
    const status = data?.source?.etat_droit_acces
    if (status === PCE_DDA_STATUS.ACTIVE) {
        return "text-green-600"
    }
    if (status === PCE_DDA_STATUS.A_VALIDER) {
        return "text-orange-500"
    }
    return "text-red-600"
}

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Gère le clic sur une cellule du tableau
 * @param payload - Données du clic (data, field, rowIndex, _blank)
 */
function onClickCell({
    data,
    field,
}: {
    data: ConsentementLegacy
    field: string
    rowIndex: number
    _blank?: boolean
}): void {
    if (field === "sourceIdsource" && data?.source?._id_source) {
        redirectWrapper("/source/:_id_source/data", {
            _id_source: data.source?._id_source,
        })
    }
}

/**
 * Copie le numéro du compteur dans le presse-papier
 */
function onClickCopyIdSource(): void {
    const idsource = selectedConsent.value?.source?.idsource || ""
    copy(idsource)

    bus.emit({
        severity: "success",
        title: t("tooltip.copy.notification"),
        life: 3000,
    })
}

/**
 * Ouvre le menu contextuel pour un consentement
 * @param event - Événement de clic
 * @param consent - Consentement sélectionné
 */
function onToggleMenu(event: Event, consent: ConsentementLegacy): void {
    selectedConsent.value = consent
    menu.value.toggle(event)
}
</script>

<template>
    <BaseTable
        :columns="getColumns"
        :value="getTableData"
        data-key="_id"
        paginator
        :rows="rowsPerPage"
        :rows-per-page-options="[10, 20, 50]"
        @click:cell="onClickCell"
        :empty-message="t('empty_list')"
        search
    >
        <!-- ================================ -->
        <!-- COLONNE TYPE -->
        <!-- ================================ -->
        <template #cell_sourceTypeForFilter="{ data }">
            <p
                class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ data.sourceIdtype }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE TITULAIRE -->
        <!-- ================================ -->
        <template #cell_titulaireForFilter="{ data }">
            <p
                class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
            >
                {{ data.titulaire }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE ÉTAT -->
        <!-- ================================ -->
        <template #cell_stateForFilter="{ data }">
            <p
                :class="[
                    'pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis',
                    getStatusClassConsent(data),
                ]"
            >
                {{ data.state }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE ACCÈS GRDF -->
        <!-- ================================ -->
        <template #cell_grdfAccessForFilter="{ data }">
            <p
                :class="[
                    'pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis',
                    getStatusClassDDA(data),
                ]"
            >
                {{ data.grdfAccess }}
            </p>
        </template>

        <!-- ================================ -->
        <!-- COLONNE ACTIONS -->
        <!-- ================================ -->
        <template #cell_actions="{ data }">
            <BaseButton
                variant="link"
                size="small"
                class="ml-auto"
                @click="onToggleMenu($event, data)"
                icon="pi pi-ellipsis-h"
            >
            </BaseButton>
        </template>
    </BaseTable>

    <!-- ================================ -->
    <!-- MENU CONTEXTUEL -->
    <!-- ================================ -->
    <BaseMenu ref="menu" :model="getMenuItems" :popup="true" />

    <!-- ================================ -->
    <!-- MODALE DE RÉVOCATION -->
    <!-- ================================ -->

    <DialogMeterRevokeConsent
        v-model:visible="modalMeterRevokeConsentToggle"
        :consent-id="selectedConsent?._id"
        @update="onRevokeConfirmed"
    />

    <SourceExtendDialog
        v-if="extendDialogMeter"
        v-model:visible="extendDialogVisible"
        :meter="extendDialogMeter"
        :building-id="extendDialogBuildingId"
        :is-consent-holder="getIsExtendConsentHolder"
        @update:consent="onSourceExtendUpdateConsent"
        @done="onExtendDialogDone"
        @cancel="onExtendDialogCancel"
    />

    <SourceChangeDialog
        v-if="changeOwnerDialogMeter"
        v-model:visible="changeOwnerDialogVisible"
        :meter="changeOwnerDialogMeter"
        :building-id="changeOwnerDialogBuildingId"
        @done="onChangeOwnerDialogDone"
        @cancel="onChangeOwnerDialogCancel"
    />
</template>

<style scoped>
/* ================================ */
/* GESTION DU TEXTE ELLIPSIS */
/* ================================ */
:deep(.text-ellipsis) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

:deep(.p-datatable-tbody > tr > td.text-ellipsis) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ================================ */
/* ESPACEMENT DES CELLULES */
/* ================================ */
:deep(.p-datatable-tbody > tr > td) {
    padding: 0.3rem 0.85rem;
}

:deep(.p-datatable-thead > tr > th) {
    padding: 0.3rem 0.85rem;
}
</style>
